import { useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import { USER_ROLE } from "Constants/constant";
import { getUser } from "store/selector/auth.selector";
import { canUseVertex360Copilot } from "helpers/spAiFeatureAccess";
import { useVertex360CopilotAccess } from "./useVertex360CopilotAccess";

const VERTEX360_COPILOT_ALLOWED_ROLES = [USER_ROLE.Sp_Admin, USER_ROLE.Sp_manager];
const VERTEX360_COPILOT_FULLSCREEN_ROUTE = "/copilot";

/** OTP / 2FA screens — Copilot stays hidden until user reaches the main app (e.g. dashboard). */
const VERTEX360_COPILOT_OTP_ROUTES = [
  "/select-otp",
  "/setup-otp",
  "/confirm-otp",
  "/verify-otp",
  "/verified-otp",
];

const isCopilotOtpRoute = (pathname) =>
  VERTEX360_COPILOT_OTP_ROUTES.some(
    (route) => pathname === route || pathname.startsWith(`${route}/`)
  );

/** True while local `step` indicates an in-progress OTP / 2FA setup or verification flow. */
export const isCopilotOtpFlowActive = () => {
  try {
    const step = localStorage.getItem("step");
    return Boolean(step && step !== "false");
  } catch {
    return false;
  }
};

/**
 * Vertex360 Copilot floating assistant gate.
 * Requires an allowed role (SP Admin / manager) AND the org+permission gate
 * (`ai_enabled` + `ai_copilot.enabled` + `ai.copilot.query`; admins bypass role perm).
 * Workers (sp_standard_user) are excluded for now.
 * @param {object | null | undefined} user
 */
export const isVertex360CopilotFloatingVisible = (user) => {
  if (!user?.id) return false;
  if (!VERTEX360_COPILOT_ALLOWED_ROLES.includes(user.userType)) return false;
  return canUseVertex360Copilot();
};

export const useVertex360CopilotFloatingVisibility = () => {
  const currentUser = useSelector(getUser);
  const { pathname } = useLocation();
  const canUseCopilot = useVertex360CopilotAccess();

  if (pathname === VERTEX360_COPILOT_FULLSCREEN_ROUTE) return false;
  if (isCopilotOtpRoute(pathname)) return false;
  if (isCopilotOtpFlowActive()) return false;
  if (!currentUser?.id) return false;
  if (!VERTEX360_COPILOT_ALLOWED_ROLES.includes(currentUser.userType)) {
    return false;
  }

  return canUseCopilot;
};
